import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { Suspense, useEffect, useRef, useState, type FormEvent, type RefObject } from 'react'
import {
  ArrowLeft,
  Check,
  ChevronDown,
  CircleUserRound,
  Eye,
  Lock,
  LogOut,
  Moon,
  PanelLeft,
  Sun,
  Users,
} from 'lucide-react'
import { clearOfflineSigning, offlineSigningStatus, storeOfflineSigning, useNetState, useOfflineRuntime, type OfflineSigningStatus, type VaultRecord } from '@floorfile/offline'
import { Button, Dialog, useMediaQuery, usePopover } from '@floorfile/ui'
import { Copy, useCopy, useFormat } from '@floorfile/i18n'
import { cn } from '@/lib/utils'
import { FeatureErrorBoundary } from '@/components/FeatureErrorBoundary'
import { NewVersionBanner } from '@/components/NewVersionBanner'
import { useAuth } from '@/lib/auth'
import { useRouteScrollReset } from '@/lib/route-scroll'
import { useDbOutage } from '@/lib/outage'
import { registerAllOfflineManifests } from '@/features/offline'
import { useEsignScope, useIssueOfflineCredential, useRevokeOfflineCredential } from '@/features/esign'
import { SidebarNav } from './SidebarNav'
import { getNavEntries } from '../nav-registry'
import { resolveTheme, useAppShell, type Density } from '../store'
import { useModules } from '@/lib/modules'
import { moduleForWebPath, permissionForWebPath } from '../module-paths'
import { pageKeyForWebPath, useCompanyConfig } from '@/lib/company-config'
import { CompanyLogo } from './CompanyLogo'
import { StaleUpdateDialog } from '../components/StaleUpdateDialog'
import { appCopy } from '../copy'
import { lazyRoute } from '@/lib/lazy-route'

/**
 * The header's feature-owned widgets, and the two safety dialogs, as lazy
 * chunks — the 2026-08-02 initial-chunk audit found this cluster was ~11% of
 * the entry chunk and growing with every slice that gets migrated. See
 * `HeaderActions.tsx` for the full reasoning and for why it is one chunk
 * rather than eight.
 *
 * `lazyRoute` rather than bare `React.lazy`: an autoUpdate service worker
 * deletes the previous precache out from under open tabs, and a header chunk
 * is exactly as vulnerable to that as a route chunk.
 */
const HeaderActions = lazyRoute(() =>
  import('./HeaderActions').then((m) => ({ default: m.HeaderActions })),
)
const RequestHelpDialog = lazyRoute(() =>
  import('@/features/incidents').then((m) => ({ default: m.RequestHelpDialog })),
)
const ReportIncidentDialog = lazyRoute(() =>
  import('@/features/incidents').then((m) => ({ default: m.ReportIncidentDialog })),
)

// Every module's D24 owner declaration enters the shared runtime once.
registerAllOfflineManifests()

const inputCls = 'w-full rounded-md border bg-background px-3 py-2 text-sm'

/**
 * Offline security dialog (D24 §9): enable/change/disable this user's
 * device PIN. A PIN only unlocks the LOCAL data key — it is never a server
 * credential, so none of these actions touch the live session.
 */
function OfflineSecurityDialog({
  open,
  onClose,
  restoreFocusRef,
}: {
  open: boolean
  onClose: () => void
  restoreFocusRef?: RefObject<HTMLElement | null>
}) {
  const { offlineEncryption, requireOfflineEncryption, enableOfflineEncryption, changeOfflinePin, disableOfflineEncryption, user } =
    useAuth()
  // FF-AUDIT-089 (owner decision 2026-09-13, option C): offline Part 11
  // signing on this device — a credential issued online after a full
  // re-authentication, sealed here under the signer's password.
  const esignScope = useEsignScope()
  const runtime = useOfflineRuntime()
  const issueCredential = useIssueOfflineCredential()
  const revokeCredential = useRevokeOfflineCredential()
  const [signing, setSigning] = useState<OfflineSigningStatus | undefined>(undefined)
  const [signPassword, setSignPassword] = useState('')
  const [signTotp, setSignTotp] = useState('')
  const [signBusy, setSignBusy] = useState(false)
  const [signMessage, setSignMessage] = useState<{ ok: boolean; text: string } | undefined>(undefined)
  useEffect(() => {
    if (!open || !user) return
    void offlineSigningStatus(runtime.db, user.id).then(setSigning).catch(() => setSigning(undefined))
  }, [open, user, runtime, offlineEncryption.enrolled])
  const sc = useCopy(appCopy).offlineSigning
  const fmt = useFormat()
  const [pin, setPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [oldPin, setOldPin] = useState('')
  const [newPin, setNewPin] = useState('')
  const [confirmNewPin, setConfirmNewPin] = useState('')
  const [confirmingDisable, setConfirmingDisable] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | undefined>(undefined)
  const c = useCopy(appCopy).offlineSecurity

  async function submitEnableSigning(event: FormEvent) {
    event.preventDefault()
    if (!user) return
    setSignMessage(undefined)
    setSignBusy(true)
    try {
      const issued = await issueCredential.mutateAsync({
        password: signPassword,
        ...(signTotp ? { totpCode: signTotp } : {}),
        deviceLabel: typeof navigator !== 'undefined' && navigator.platform ? navigator.platform.slice(0, 120) : undefined,
      })
      const stored = await storeOfflineSigning(
        runtime.db,
        user.id,
        { credentialId: issued.data.id, signingKey: issued.data.signingKey, expiresAt: issued.data.expiresAt, reauthMethod: issued.data.reauthMethod },
        signPassword,
      )
      setSigning(stored)
      setSignPassword('')
      setSignTotp('')
      setSignMessage({ ok: true, text: sc.enabled })
    } catch (err) {
      setSignMessage({ ok: false, text: err instanceof Error ? err.message : sc.couldNotEnable })
    } finally {
      setSignBusy(false)
    }
  }

  async function disableSigning() {
    if (!user) return
    setSignMessage(undefined)
    setSignBusy(true)
    try {
      if (signing) await revokeCredential.mutateAsync(signing.credentialId).catch(() => undefined) // the server copy may already be gone; the device copy must go regardless
      await clearOfflineSigning(runtime.db, user.id)
      setSigning(undefined)
      setSignMessage({ ok: true, text: sc.disabled })
    } catch (err) {
      setSignMessage({ ok: false, text: err instanceof Error ? err.message : sc.couldNotDisable })
    } finally {
      setSignBusy(false)
    }
  }

  function reset() {
    setSignPassword('')
    setSignTotp('')
    setSignMessage(undefined)
    setPin('')
    setConfirmPin('')
    setOldPin('')
    setNewPin('')
    setConfirmNewPin('')
    setConfirmingDisable(false)
    setMessage(undefined)
  }

  async function submitEnable(event: FormEvent) {
    event.preventDefault()
    setMessage(undefined)
    if (pin !== confirmPin) {
      setMessage({ ok: false, text: c.pinsDoNotMatch })
      return
    }
    setBusy(true)
    try {
      await enableOfflineEncryption(pin)
      setMessage({ ok: true, text: c.enabled })
      setPin('')
      setConfirmPin('')
    } catch (err) {
      setMessage({
        ok: false,
        text: err instanceof Error ? err.message : c.couldNotEnable,
      })
    } finally {
      setBusy(false)
    }
  }

  async function submitChangePin(event: FormEvent) {
    event.preventDefault()
    setMessage(undefined)
    if (newPin !== confirmNewPin) {
      setMessage({ ok: false, text: c.newPinsDoNotMatch })
      return
    }
    setBusy(true)
    try {
      await changeOfflinePin(oldPin, newPin)
      setMessage({ ok: true, text: c.pinChanged })
      setOldPin('')
      setNewPin('')
      setConfirmNewPin('')
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : c.couldNotChange })
    } finally {
      setBusy(false)
    }
  }

  async function submitDisable() {
    setBusy(true)
    setMessage(undefined)
    try {
      await disableOfflineEncryption()
      setMessage({ ok: true, text: c.disabled })
      setConfirmingDisable(false)
    } catch (err) {
      setMessage({
        ok: false,
        text: err instanceof Error ? err.message : c.couldNotDisable,
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset()
        onClose()
      }}
      title={c.title}
      description={c.description}
      {...(restoreFocusRef ? { restoreFocusRef } : {})}
    >
      <div className="space-y-4 text-sm">
        <p>
          {c.statusLabel}{' '}
          <span className={offlineEncryption.enrolled ? 'font-medium text-success' : 'font-medium text-muted-foreground'}>
            {offlineEncryption.enrolled ? c.on : c.off}
          </span>
        </p>

        {message && <p className={message.ok ? 'text-success' : 'text-destructive'}>{message.text}</p>}
        {/* FF-AUDIT-098: the launch rule, said where the PIN is set. */}
        {offlineEncryption.enrolled && <p className="text-xs text-muted-foreground">{c.askedEveryLaunch}</p>}

        {esignScope.enabled && offlineEncryption.enrolled && (
          <section className="space-y-2 rounded-md border p-3">
            <h3 className="font-medium">{sc.heading}</h3>
            <p className="text-xs text-muted-foreground">{sc.description}</p>
            <p>
              {sc.statusLabel}{' '}
              <span className={signing ? 'font-medium text-success' : 'font-medium text-muted-foreground'}>
                {signing ? sc.onUntil({ when: fmt.date(signing.expiresAt) }) : sc.off}
              </span>
            </p>
            {signMessage && <p className={signMessage.ok ? 'text-success' : 'text-destructive'}>{signMessage.text}</p>}
            {!signing ? (
              <form onSubmit={(e) => void submitEnableSigning(e)} className="space-y-2">
                <input aria-label={sc.password}
                  required
                  type="password"
                  autoComplete="current-password"
                  placeholder={sc.password}
                  value={signPassword}
                  onChange={(e) => setSignPassword(e.target.value)}
                  className={inputCls}
                />
                {user?.totpEnabled && (
                  <input aria-label={sc.authenticatorCode}
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder={sc.authenticatorCode}
                    value={signTotp}
                    onChange={(e) => setSignTotp(e.target.value)}
                    className={inputCls}
                  />
                )}
                <Button type="submit" pending={signBusy} disabled={!signPassword}>
                  {signBusy ? sc.enabling : sc.enable}
                </Button>
              </form>
            ) : (
              <Button variant="outline" pending={signBusy} onClick={() => void disableSigning()}>
                {signBusy ? sc.disabling : sc.disable}
              </Button>
            )}
          </section>
        )}

        {!offlineEncryption.enrolled ? (
          <form onSubmit={(e) => void submitEnable(e)} className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {c.choosePin}
            </p>
            <input aria-label={c.newPin}
              required
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder={c.newPin}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className={inputCls}
            />
            <input aria-label={c.confirmPin}
              required
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder={c.confirmPin}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value)}
              className={inputCls}
            />
            <Button type="submit" pending={busy}>
              {busy ? c.enabling : c.enable}
            </Button>
          </form>
        ) : (
          <>
            <form onSubmit={(e) => void submitChangePin(e)} className="space-y-2">
              <h3 className="font-medium">{c.changePinHeading}</h3>
              <input aria-label={c.currentPin}
                required
                type="password"
                inputMode="numeric"
                autoComplete="off"
                placeholder={c.currentPin}
                value={oldPin}
                onChange={(e) => setOldPin(e.target.value)}
                className={inputCls}
              />
              <input aria-label={c.newPin}
                required
                type="password"
                inputMode="numeric"
                autoComplete="off"
                placeholder={c.newPin}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                className={inputCls}
              />
              <input aria-label={c.confirmNewPin}
                required
                type="password"
                inputMode="numeric"
                autoComplete="off"
                placeholder={c.confirmNewPin}
                value={confirmNewPin}
                onChange={(e) => setConfirmNewPin(e.target.value)}
                className={inputCls}
              />
              <Button type="submit" pending={busy}>
                {busy ? c.changing : c.changePin}
              </Button>
            </form>

            <div className="border-t pt-3">
              {requireOfflineEncryption ? (
                <p className="text-xs text-muted-foreground">{c.required}</p>
              ) : confirmingDisable ? (
                <div className="space-y-2">
                  <p className="text-sm text-destructive">{c.disableWarning}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10"
 disabled={busy}
 onClick={() => void submitDisable()}

 >
                      {busy ? c.disabling : c.confirmDisable}
                    </Button>
                    <Button variant="outline"
 onClick={() => setConfirmingDisable(false)}

 >
                      {c.cancel}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10"
 onClick={() => setConfirmingDisable(true)}

 >
                  {c.disable}
                </Button>
              )}
            </div>
          </>
        )}
        {/* FF-AUDIT-098: a Done control — the only other exits were the
            overlay and "Disable", and an operator read "Disable" as the way
            to close the dialog. */}
        <div className="flex justify-end border-t pt-3">
          <Button
            onClick={() => {
              reset()
              onClose()
            }}
          >
            {c.done}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

/**
 * Switch-user dialog (D24 §9 quick user switching, shared tablets): unlocks
 * another enrolled user's data key with their PIN and applies their
 * identity. This is OFFLINE-AUTHENTICATED for the switched-to user — no
 * server session is fabricated; syncing their queued work still needs a
 * real sign-in via /login.
 */
function SwitchUserDialog({
  open,
  onClose,
  restoreFocusRef,
}: {
  open: boolean
  onClose: () => void
  restoreFocusRef?: RefObject<HTMLElement | null>
}) {
  const { user, listDeviceUsers, switchUser } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState<VaultRecord[]>([])
  const [loaded, setLoaded] = useState(false)
  const [selected, setSelected] = useState<VaultRecord | undefined>(undefined)
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | undefined>(undefined)
  const c = useCopy(appCopy).switchUser

  useEffect(() => {
    if (!open) {
      setLoaded(false)
      setSelected(undefined)
      setPin('')
      setMessage(undefined)
      return undefined
    }
    let cancelled = false
    void listDeviceUsers().then((records) => {
      if (!cancelled) {
        setUsers(records.filter((record) => record.userId !== user?.id))
        setLoaded(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [open, listDeviceUsers, user?.id])

  async function submitSwitch(event: FormEvent) {
    event.preventDefault()
    if (!selected) return
    setMessage(undefined)
    setBusy(true)
    try {
      await switchUser(selected.userId, pin)
      onClose()
      navigate('/')
    } catch (err) {
      setMessage(err instanceof Error ? err.message : c.pinNotAccepted)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={c.title} description={c.description} {...(restoreFocusRef ? { restoreFocusRef } : {})}>
      <div className="space-y-3 text-sm">
        {!loaded ? (
          <p className="text-muted-foreground">{c.loading}</p>
        ) : selected ? (
          <form onSubmit={(e) => void submitSwitch(e)} className="space-y-2">
            <p>
              {/* One message, one slot — never `'Enter the PIN for ' + <span>`.
                  A concatenated sentence forces the translator to work with
                  fragments whose order French may not allow. */}
              <Copy
                of={c.enterPinFor({
                  name: `${selected.firstName} ${selected.lastName}`,
                  email: selected.email,
                })}
                components={[<span className="font-medium" key="0" />]}
              />
            </p>
            <input aria-label={c.pin}
              required
              autoFocus
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder={c.pin}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className={inputCls}
            />
            {message && <p className="text-destructive">{message}</p>}
            <div className="flex gap-2">
              <Button type="submit" pending={busy}>
                {busy ? c.switching : c.submit}
              </Button>
              <Button variant="outline"
 onClick={() => {
 setSelected(undefined)
 setPin('')
 setMessage(undefined)
 }}

 >
                {c.back}
              </Button>
            </div>
          </form>
        ) : users.length > 0 ? (
          <ul className="divide-y">
            {users.map((record) => (
              <li key={record.userId}>
                <Button
                  variant="ghost"
                  onClick={() => setSelected(record)}
                  className="h-auto w-full flex-col items-start gap-0.5 whitespace-normal px-2 py-2 text-left font-normal"
                >
                  <span className="font-medium">
                    {record.firstName} {record.lastName}
                  </span>
                  <span className="text-xs text-muted-foreground">{record.email}</span>
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">{c.none}</p>
        )}

        <div className="border-t pt-3">
          <Button variant="link" size="inline" className="font-medium"
 onClick={() => {
 onClose()
 navigate('/login')
 }}

 >
            {c.addAnother}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

export function AppShell() {
  const { sidebarOpen, toggleSidebar, theme, setTheme, density, setDensity } = useAppShell()
  const { permissions, user, impersonation, offlineAuthenticated, logout, endImpersonation, authFetch } = useAuth()
  const offlineRuntime = useOfflineRuntime()
  const netState = useNetState()
  const modules = useModules()
  const companyConfig = useCompanyConfig()
  const location = useLocation()
  // FF-QA-004: `document.title` was "FloorFile" on every page — no tab, history
  // or screen-reader landmark could tell two pages apart. The longest nav
  // route that prefixes the path names the page.
  const navItems = useCopy(appCopy).nav.items as Record<string, string>
  useEffect(() => {
    const path = location.pathname
    let match = ''
    for (const route of Object.keys(navItems)) {
      if (route !== '/' && route.length > match.length && (path === route || path.startsWith(`${route}/`))) match = route
    }
    document.title = match ? `${navItems[match]} · FloorFile` : 'FloorFile'
  }, [location.pathname, navItems])
  const routeModule = moduleForWebPath(location.pathname)
  // H-11: the route gate derives from the same registry as the sidebar.
  const routePermission = permissionForWebPath(location.pathname, getNavEntries())
  const routePageKey = pageKeyForWebPath(location.pathname)
  const navigate = useNavigate()
  // FF-AUDIT-001: a new route lands at the top with focus on the content.
  const mainRef = useRef<HTMLElement>(null)
  useRouteScrollReset(mainRef)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  // FF-AUDIT-20260914-210459-087: the sidebar may expand INLINE only from
  // `lg` (1024 px) up. Between 768 and 1023 px the stored "expanded"
  // preference used to keep the 240-px rail open and leave 528 px for the
  // content — KPI rows of 2+2+1, check numbers over four lines. There the
  // rail is icons, and the same toggle opens the drawer the phone uses.
  const wide = useMediaQuery('(min-width: 1024px)')
  const sidebarExpanded = sidebarOpen && wide
  // FF-AUDIT-20260914-210459-028: the account menu is a header popover like the other three.
  const { open: userMenuOpen, setOpen: setUserMenuOpen, containerRef: accountMenuRef, panelRef: accountMenuPanelRef } = usePopover()
  const [offlineSecurityOpen, setOfflineSecurityOpen] = useState(false)
  const [switchUserOpen, setSwitchUserOpen] = useState(false)
  /**
   * The phone safety dialogs are owned HERE, not by the overflow menu's
   * children (audit FF31-05). `HeaderOverflowMenu` unmounts its popup on
   * dismiss, so a dialog owned inside it is destroyed by the same click that
   * opens it — which is exactly what made `Request help` and `Report incident`
   * unopenable below `sm`. This shell is always mounted, so it can hold them.
   */
  const [safetyAction, setSafetyAction] = useState<'help' | 'incident' | null>(null)
  /**
   * The disclosure that opened those dialogs, for focus restore. The ROW the
   * user pressed is inside a popup that unmounts as the dialog opens, so it is
   * detached by the time `Dialog` would restore focus and focus falls to
   * <body> — the phone twin of the account-menu defect fixed on 2026-07-23.
   * This trigger is always mounted, and is what the user pressed to get here.
   */
  const overflowTriggerRef = useRef<HTMLButtonElement>(null)
  // The account-menu dialogs are opened from menu items that unmount on select,
  // so focus is returned to this always-mounted toggle button on close (a11y).
  const accountMenuButtonRef = useRef<HTMLButtonElement>(null)
  const dbOutage = useDbOutage()
  const c = useCopy(appCopy)
  const navEntries = getNavEntries().filter(
    (entry) => {
      const pageKey = pageKeyForWebPath(entry.path)
      if (!companyConfig.isPageAllowed(pageKey)) return false
      if (pageKey === 'chat' && !companyConfig.chatEnabled) return false
      if (pageKey === 'ai' && !companyConfig.aiEnabled) return false
      return (!entry.permission || permissions.has(entry.permission)) && (!entry.moduleKey || modules.isEnabled(entry.moduleKey))
    },
  )

  return (
    <div className="flex min-h-screen min-w-0">
      <aside aria-label={c.shell.sidebar}
        className={cn(
          // Sticky and viewport-bounded (FF-AUDIT-001): the aside used to
          // stretch to the document's height, so its nav never scrolled and
          // the lower entries were reached by scrolling the whole page.
          'sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-all md:flex',
          sidebarExpanded ? 'w-60' : 'w-14',
        )}
      >
        {/* The title is the way home (AUDIT_FINDINGS M-11). */}
        <Link to="/" className="flex h-14 items-center gap-2 px-4 font-semibold hover:text-sidebar-primary" aria-label={c.shell.goToDashboard}>
          <CompanyLogo path={companyConfig.logoUrl} alt={c.shell.companyLogo} className="h-8 w-8 rounded object-contain" />
          {sidebarExpanded && <span>{c.shell.brand}</span>}
        </Link>
        <nav className="flex-1 overflow-y-auto px-2 py-2">
          <SidebarNav entries={navEntries} expanded={sidebarExpanded} onNavigate={undefined} />
        </nav>
      </aside>

      {mobileNavOpen && (
        <Dialog open onClose={() => setMobileNavOpen(false)} title={c.shell.navigation} titleVisuallyHidden unstyled containerClassName="justify-start p-0 lg:hidden" contentClassName="mt-0" className="h-full max-h-none w-64 max-w-64">
          <aside aria-label={c.shell.sidebar} className="relative flex h-full w-64 flex-col bg-sidebar text-sidebar-foreground shadow-xl">
            <div className="flex h-14 items-center gap-2 px-4 font-semibold">
              <Link to="/" onClick={() => setMobileNavOpen(false)} className="flex items-center gap-2 hover:text-sidebar-primary" aria-label={c.shell.goToDashboard}>
                <CompanyLogo path={companyConfig.logoUrl} alt={c.shell.companyLogo} className="h-8 w-8 rounded object-contain" />
                <span>{c.shell.brand}</span>
              </Link>
              <Button variant="ghost" size="icon" aria-label={c.shell.closeNavigation} onClick={() => setMobileNavOpen(false)} className="ml-auto hover:bg-sidebar-accent hover:text-sidebar-foreground">×</Button>
            </div>
            <nav className="flex-1 overflow-y-auto px-2 py-2">
              <SidebarNav entries={navEntries} expanded onNavigate={() => setMobileNavOpen(false)} />
            </nav>
          </aside>
        </Dialog>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {dbOutage && (
          <div className="border-b border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-foreground sm:px-4" role="status">
            <Copy of={c.banner.dbOutage} components={[<span className="font-semibold" key="0" />]} />
          </div>
        )}
        {impersonation && (
          <div className="border-b border-warning/50 bg-warning/10 px-3 py-2 text-sm text-foreground sm:px-4">
            <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <Eye className="h-4 w-4 shrink-0 text-warning" />
                <span className="font-semibold">{c.banner.impersonation}</span>
                <span className="truncate text-muted-foreground">
                  {impersonation.companyName} · {impersonation.reason}
                </span>
              </div>
              <Button variant="outline" size="sm" className="inline-flex items-center gap-1 border-warning/50 font-medium"
 onClick={() => {
 const returnTo = impersonation.returnTo
 endImpersonation()
 window.location.assign(returnTo)
 }}

 >
                <ArrowLeft className="h-3.5 w-3.5" /> {c.banner.returnToAdmin}
              </Button>
            </div>
          </div>
        )}
        {offlineAuthenticated && netState !== 'offline' && (
          <div className="border-b border-warning/50 bg-warning/10 px-3 py-2 text-sm text-foreground sm:px-4" role="status">
            <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2">
              <span className="truncate">
                {c.banner.offlineSignedIn({ name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() })}
              </span>
              <Link
                to="/login"
                className="shrink-0 rounded-md border border-warning/50 bg-background px-2.5 py-1 font-medium hover:bg-accent"
              >
                {c.banner.signInToSync}
              </Link>
            </div>
          </div>
        )}
        {/* `gap-0 px-1` below `sm`, measured rather than guessed. At 375 px the
            nine header controls plus `gap-1` and `px-2` came to 406 px, so EVERY
            page scrolled horizontally on a phone — the shell, not any one
            register, was the overflow. Tightening the spacing recovers 56 px
            and lands at 350. Deliberately not solved by hiding a control:
            dropping the offline indicator or the facility switcher on the
            narrowest screen is a product decision, and this is a layout bug.
            The buttons carry their own padding, so the 44 px tap floor is
            untouched. */}
        {/* Phones (2026-09-19): nine icon controls at the touch control size
            (44 px) are 396 px — wider than a 375 px phone — so flex shrank
            every button to 16 px and the row still overflowed. Below `sm` the
            icon buttons go back to 36 px (the hit area stays 44 px through
            the D28 pseudo) and the row clips its x overflow, because that
            pseudo's overhang on the last button is scrollable overflow.
            Clip x only: the account and overflow menus hang below the row. */}
        <header className="flex h-14 items-center gap-0 overflow-x-clip border-b bg-background px-1 max-sm:[&_.w-control]:w-9 sm:gap-2 sm:px-4">
          <Button
            variant="ghost"
            size="icon"
            // FF-AUDIT-087: on a tablet the rail cannot expand inline, so the
            // toggle opens the navigation drawer instead.
            onClick={() => (wide ? toggleSidebar() : setMobileNavOpen(true))}
            aria-label={wide ? c.shell.toggleSidebar : c.shell.openNavigation}
            className="hidden md:inline-flex"
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileNavOpen(true)}
            aria-label={c.shell.openNavigation}
            className="md:hidden"
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
          {/* The fallback reserves the row's flex behaviour and the icons'
              width, so the header does not reflow when the chunk lands. It is
              deliberately empty rather than a set of skeleton pills: these are
              controls, and a placeholder that looks pressable is worse than a
              gap for the ~1 request it is on screen. */}
          <Suspense
            fallback={
              <>
                <div className="hidden min-w-0 flex-1 lg:flex lg:px-4" />
                <div className="flex-1 lg:hidden" />
                <div aria-hidden className="h-8 w-8 shrink-0" />
              </>
            }
          >
            <HeaderActions
              impersonation={Boolean(impersonation)}
              overflowTriggerRef={overflowTriggerRef}
              onSafetyAction={setSafetyAction}
            />
          </Suspense>
          {impersonation ? (
            <Button variant="ghost" size="iconSm"
 onClick={() => {
 const returnTo = impersonation.returnTo
 endImpersonation()
 window.location.assign(returnTo)
 }}
 aria-label={c.banner.returnToAdmin}

 >
              <LogOut className="h-4 w-4" />
            </Button>
          ) : (
            <div className="relative" ref={accountMenuRef}>
              <Button
                ref={accountMenuButtonRef}
                variant="ghost"
                onClick={() => setUserMenuOpen((open) => !open)}
                aria-label={c.shell.accountMenu}
                aria-expanded={userMenuOpen}
                className="gap-1.5 px-2 font-normal"
              >
                {/* FF-AUDIT-023: the name only from lg — at tablet width the
                    icon keeps the row inside the viewport. */}
                <span className="hidden text-muted-foreground lg:inline">
                  {user?.firstName} {user?.lastName}
                </span>
                <CircleUserRound className="h-4 w-4 lg:hidden" />
                <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground lg:block" />
              </Button>
              {userMenuOpen && (
                <>
                  <Button
                    variant="ghost"
                    size="inline"
                    aria-label={c.shell.closeAccountMenu}
                    className="fixed inset-0 z-40 cursor-default rounded-none hover:bg-transparent"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div ref={accountMenuPanelRef} role="menu" aria-label={c.shell.accountMenu} className="absolute right-0 top-full z-50 mt-1 w-52 rounded-md border bg-popover p-1 text-sm shadow-md">
                    <div className="px-2 py-1.5">
                      <p className="truncate font-medium">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                    <div className="my-1 border-t" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setUserMenuOpen(false)
                        navigate('/account')
                      }}
                      role="menuitem"
                      className="w-full justify-start gap-2 px-2 font-normal"
                    >
                      <CircleUserRound className="h-4 w-4" /> {c.account.settings}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setUserMenuOpen(false)
                        setOfflineSecurityOpen(true)
                      }}
                      role="menuitem"
                      className="w-full justify-start gap-2 px-2 font-normal"
                    >
                      <Lock className="h-4 w-4" /> {c.account.offlineSecurity}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setUserMenuOpen(false)
                        setSwitchUserOpen(true)
                      }}
                      role="menuitem"
                      className="w-full justify-start gap-2 px-2 font-normal"
                    >
                      <Users className="h-4 w-4" /> {c.account.switchUser}
                    </Button>
                    <div className="my-1 border-t" />
                    {/* Density (Fiix audit §8.2): per device, like the theme. */}
                    <DensityChoice
                      label={c.account.density}
                      value={density}
                      onChange={setDensity}
                      options={[
                        { id: 'auto', label: c.account.densityAuto },
                        { id: 'comfortable', label: c.account.densityComfortable },
                        { id: 'compact', label: c.account.densityCompact },
                      ]}
                    />
                    <div className="my-1 border-t" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setUserMenuOpen(false)
                        // Explicit logout clears this user's cached partition
                        // (D24 §9); queued unsynced work stays tied to their id.
                        void offlineRuntime
                          .clearPartition()
                          .then(() => logout())
                          .then(() => navigate('/login'))
                      }}
                      role="menuitem"
                      className="w-full justify-start gap-2 px-2 font-normal"
                    >
                      <LogOut className="h-4 w-4" /> {c.account.signOut}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              const next = resolveTheme(theme) === 'dark' ? 'light' : 'dark'
              setTheme(next)
              void authFetch('/api/v1/settings/profile', {
                method: 'PATCH', headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ theme: next.toUpperCase() }),
              })
            }}
            aria-label={c.shell.toggleTheme}
          >
            {resolveTheme(theme) === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </header>
        {/* tabIndex -1 so route changes can move focus here (FF-AUDIT-001). */}
        <main ref={mainRef} tabIndex={-1} className="min-w-0 flex-1 p-3 outline-none sm:p-6">
          {companyConfig.status === 'loading' ? (
            <p className="text-sm text-muted-foreground">{c.gate.loadingCompanyAccess}</p>
          ) : routePageKey && !companyConfig.isPageAllowed(routePageKey) ? (
            <section className="mx-auto max-w-xl rounded-lg border bg-card p-6">
              <h1 className="text-xl font-semibold">{c.gate.noAccessTitle}</h1>
              {/* FF-AUDIT-080: one refusal for both gates — the matrix and the
                  permission are one question to the person refused. */}
              <p className="mt-2 text-sm text-muted-foreground">{c.gate.noAccessArea}</p>
              <Link to="/" className="mt-4 inline-flex text-sm font-medium text-primary hover:underline">{c.shell.goToDashboard}</Link>
            </section>
          ) : routePageKey === 'chat' && !companyConfig.chatEnabled ? (
            <section className="mx-auto max-w-xl rounded-lg border bg-card p-6"><h1 className="text-xl font-semibold">{c.gate.featureUnavailable}</h1><p className="mt-2 text-sm text-muted-foreground">{c.gate.chatDisabled}</p></section>
          ) : routePageKey === 'ai' && !companyConfig.aiEnabled ? (
            <section className="mx-auto max-w-xl rounded-lg border bg-card p-6"><h1 className="text-xl font-semibold">{c.gate.featureUnavailable}</h1><p className="mt-2 text-sm text-muted-foreground">{c.gate.aiDisabled}</p></section>
          ) : routeModule && modules.status === 'loading' ? (
            <p className="text-sm text-muted-foreground">{c.gate.loadingModuleAccess}</p>
          ) : routeModule && !modules.isEnabled(routeModule) ? (
            <section className="mx-auto max-w-xl rounded-lg border bg-card p-6">
              <h1 className="text-xl font-semibold">{c.gate.moduleUnavailableTitle}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{c.gate.moduleUnavailableBody}</p>
              {permissions.has('settings:read') && <Link to="/settings/modules" className="mt-4 inline-flex text-sm font-medium text-primary hover:underline">{c.gate.openModuleSettings}</Link>}
            </section>
          ) : routePermission && !permissions.has(routePermission) ? (
            <section className="mx-auto max-w-xl rounded-lg border bg-card p-6">
              <h1 className="text-xl font-semibold">{c.gate.noAccessTitle}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{c.gate.noAccessArea}</p>
              <Link to="/" className="mt-4 inline-flex text-sm font-medium text-primary hover:underline">{c.shell.goToDashboard}</Link>
            </section>
          ) : (
            // Audit RUN-12: scope a page crash to the content area so the
            // sidebar, header, bell and facility switcher survive it. Keyed on
            // the pathname so navigating away clears the error.
            <FeatureErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </FeatureErrorBoundary>
          )}
        </main>
      </div>
      <OfflineSecurityDialog open={offlineSecurityOpen} onClose={() => setOfflineSecurityOpen(false)} restoreFocusRef={accountMenuButtonRef} />
      <SwitchUserDialog open={switchUserOpen} onClose={() => setSwitchUserOpen(false)} restoreFocusRef={accountMenuButtonRef} />
      {/* Phone safety dialogs — mounted here, outside the overflow menu that
          triggers them, so dismissing the menu cannot destroy them (FF31-05). */}
      {/* No fallback: these mount only after the user has acted, and a spinner
          where a dialog is about to appear reads as a second, phantom dialog.
          The chunk is already in the precache by the time anyone gets here —
          `HeaderActions` pulled the incidents slice on first paint. */}
      <Suspense fallback={null}>
        {safetyAction === 'help' && (
          <RequestHelpDialog onClose={() => setSafetyAction(null)} restoreFocusRef={overflowTriggerRef} />
        )}
        {safetyAction === 'incident' && (
          <ReportIncidentDialog onClose={() => setSafetyAction(null)} restoreFocusRef={overflowTriggerRef} />
        )}
      </Suspense>
      <StaleUpdateDialog />
      <NewVersionBanner />
    </div>
  )
}

/**
 * F16: the account menu's Density choice as a vertical radio group, one row
 * per option with a check mark — it fits the 208 px panel in any language.
 * The segmented control it replaces needed ~280 px for three English labels
 * and spilled out of the panel (French was worse).
 *
 * `radiogroup`/`radio`, not `menuitemradio`: the account panel is not a
 * `role="menu"`, and a menu item outside a menu fails the required-parent
 * rule. Arrow keys move the choice (the radio-group pattern); only the checked
 * option is in the tab order.
 */
function DensityChoice({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: Density
  onChange: (next: Density) => void
  options: { id: Density; label: string }[]
}) {
  const refs = useRef(new Map<Density, HTMLButtonElement>())
  const move = (from: Density, delta: number) => {
    const at = options.findIndex((option) => option.id === from)
    const next = options[(at + delta + options.length) % options.length]
    if (!next) return
    onChange(next.id)
    refs.current.get(next.id)?.focus()
  }
  return (
    <div className="px-1 py-1">
      <p id="account-density-label" className="px-1 pb-1 text-xs text-muted-foreground">
        {label}
      </p>
      <div role="radiogroup" aria-labelledby="account-density-label" className="flex flex-col">
        {options.map((option) => {
          const checked = option.id === value
          return (
            <Button
              key={option.id}
              ref={(node: HTMLButtonElement | null) => {
                if (node) refs.current.set(option.id, node)
                else refs.current.delete(option.id)
              }}
              variant="ghost"
              size="sm"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              onClick={() => onChange(option.id)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                  event.preventDefault()
                  move(option.id, 1)
                } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                  event.preventDefault()
                  move(option.id, -1)
                }
              }}
              className="w-full justify-start gap-2 px-2 font-normal"
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">{checked && <Check className="h-4 w-4" aria-hidden />}</span>
              <span className="min-w-0 truncate">{option.label}</span>
            </Button>
          )
        })}
      </div>
    </div>
  )
}
